import React from 'react';
import { Users, DollarSign, CheckCircle } from 'lucide-react';

interface K1Data {
  filename: string;
  year: string;
  data: {
    form_type?: string;
    tax_year?: string;
    partner_name?: string;
    schedule_k1?: {
      total_officer_compensation: number;
      partners?: Array<{
        name: string;
        box_1_ordinary_income: number;
      }>;
    };
    schedule_k?: {
      line_1_ordinary_income: number;
    };
  };
}

interface K1PartnerSummaryProps {
  tax1065Files: K1Data[];
}

export default function K1PartnerSummary({ tax1065Files }: K1PartnerSummaryProps) {
  // Extract and aggregate K-1 data by partner and year
  const aggregateK1Data = () => {
    const partnerData: Record<string, Record<string, number>> = {};
    const yearTotals: Record<string, number> = {};
    
    console.log('📊 K1PartnerSummary: Processing', tax1065Files.length, 'files');
    
    tax1065Files.forEach((file, index) => {
      const data = file.data;
      const year = data.tax_year || file.year || '2022';
      
      // Check for form_type in multiple locations (top-level and inside data)
      const formType = file.form_type || data.form_type || data.document_type;
      
      console.log(`   File ${index + 1}:`, file.filename, '| Type:', formType, '| Year:', year);
      
      // Check if this is a K-1 file (case-insensitive)
      const isK1 = formType === 'K-1' || formType === 'K1' || formType?.toLowerCase() === 'k-1';
      
      // Also check if this is a 1065 with K-1 data embedded
      const is1065WithK1Data = formType === '1065' && data.schedule_k1 && (
        data.schedule_k1.partners?.length > 0 || 
        data.schedule_k1.total_officer_compensation > 0
      );
      
      if ((isK1 || is1065WithK1Data) && data.schedule_k1) {
        // Check if there's a partners array - use that instead of single partner_name
        const partners = data.schedule_k1.partners;
        
        if (partners && Array.isArray(partners) && partners.length > 0) {
          // Multiple partners in this K-1 file - iterate through them
          console.log(`     → K-1 with ${partners.length} partner(s)`);
          
          partners.forEach(partner => {
            const partnerName = partner.name || 'Unknown Partner';
            const box1Amount = partner.box_1_ordinary_income || 0;
            
            console.log(`        • ${partnerName} = $${box1Amount}`);
            
            // Initialize partner if not exists
            if (!partnerData[partnerName]) {
              partnerData[partnerName] = {};
            }
            
            // Store amount for this year (don't overwrite if already exists)
            if (!partnerData[partnerName][year]) {
              partnerData[partnerName][year] = box1Amount;
              // Add to year total
              yearTotals[year] = (yearTotals[year] || 0) + box1Amount;
            }
          });
        } else if (data.partner_name) {
          // Single partner with explicit partner_name - only use if explicitly set
          const partnerName = data.partner_name;
          const box1Amount = data.schedule_k1.total_officer_compensation || 0;
          
          console.log(`     → K-1 found: ${partnerName} = $${box1Amount}`);
          
          // Initialize partner if not exists
          if (!partnerData[partnerName]) {
            partnerData[partnerName] = {};
          }
          
          // Store amount for this year (don't overwrite if already exists)
          if (!partnerData[partnerName][year]) {
            partnerData[partnerName][year] = box1Amount;
            // Add to year total
            yearTotals[year] = (yearTotals[year] || 0) + box1Amount;
          }
        }
        // If no partners array AND no explicit partner_name, skip this entry
        // This avoids creating "Unknown Partner" entries for 1065 form totals
      } else if (isK1 || is1065WithK1Data) {
        console.warn(`     ⚠️ K-1/1065 detected but no schedule_k1 data:`, data);
      }
    });
    
    console.log('📈 Aggregated partner data:', partnerData);
    console.log('📈 Year totals:', yearTotals);
    
    // Store 1065 totals for verification before removing
    const form1065Totals: Record<string, number> = {};
    let has1065Verification = false;
    
    // CRITICAL FIX: If we have named partners (not "Unknown Partner"), 
    // remove "Unknown Partner" as it's the 1065 combined total (for verification only)
    const namedPartners = Object.keys(partnerData).filter(name => name !== 'Unknown Partner');
    if (namedPartners.length > 0 && partnerData['Unknown Partner']) {
      console.log('📋 Using "Unknown Partner" (1065 total) for verification against K-1 individual amounts');
      has1065Verification = true;
      
      // Store 1065 totals for verification
      Object.keys(partnerData['Unknown Partner']).forEach(year => {
        form1065Totals[year] = partnerData['Unknown Partner'][year] || 0;
      });
      
      // Recalculate year totals without "Unknown Partner"
      Object.keys(partnerData['Unknown Partner']).forEach(year => {
        yearTotals[year] = (yearTotals[year] || 0) - (partnerData['Unknown Partner'][year] || 0);
      });
      
      // Remove the Unknown Partner entry from display
      delete partnerData['Unknown Partner'];
    }
    
    return { partnerData, yearTotals, form1065Totals, has1065Verification };
  };
  
  const { partnerData, yearTotals, form1065Totals, has1065Verification } = aggregateK1Data();
  const partners = Object.keys(partnerData);
  const years = Object.keys(yearTotals).sort();
  
  // Calculate grand total from K-1s
  const grandTotal = Object.values(yearTotals).reduce((sum, val) => sum + val, 0);
  
  // Calculate 1065 verification total
  const form1065GrandTotal = Object.values(form1065Totals).reduce((sum, val) => sum + val, 0);
  
  // Check if K-1s match 1065 total (within $1 tolerance for rounding)
  const verificationPassed = has1065Verification && Math.abs(grandTotal - form1065GrandTotal) < 1;
  const verificationDiff = has1065Verification ? grandTotal - form1065GrandTotal : 0;
  
  if (partners.length === 0) {
    // Count files with K-1 data (K-1 forms or 1065s with partner schedules)
    const filesWithK1Data = tax1065Files.filter(f => {
      const formType = f.form_type || f.data?.form_type;
      const isK1 = formType === 'K-1' || formType === 'K1';
      const has1065K1Data = formType === '1065' && (
        f.data?.schedule_k1?.partners?.length > 0 ||
        f.data?.schedule_k1?.total_officer_compensation > 0
      );
      return isK1 || has1065K1Data;
    });
    
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <p className="text-yellow-800 font-semibold mb-2">No K-1 Partner Data Available</p>
        <p className="text-yellow-700 text-sm mb-3">
          Uploaded files: {tax1065Files.length} total | {filesWithK1Data.length} with partner data
        </p>
        {tax1065Files.length > 0 && filesWithK1Data.length === 0 && (
          <p className="text-yellow-700 text-sm">
            💡 The uploaded files may not contain Schedule K-1 partner compensation data. 
            Make sure Form 1065 files include K-1 schedules or upload individual K-1 forms.
          </p>
        )}
        {/* Debug Info */}
        <div className="bg-white border border-yellow-300 rounded p-3 mt-3 text-left text-xs">
          <p className="font-semibold text-gray-700 mb-2">Debug Info ({tax1065Files.length} files):</p>
          {tax1065Files.slice(0, 5).map((file, i) => (
            <div key={i} className="text-gray-600 mb-1">
              • {file.filename}: Type={file.form_type || file.data?.form_type || 'unknown'}, 
              schedule_k1={file.data?.schedule_k1 ? 'present' : 'missing'},
              officer_comp=${file.data?.schedule_k1?.total_officer_compensation || 0}
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            K-1 Partner Summary
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Officer compensation from Schedule K-1 Box 1 (all partners, all years)
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs text-gray-600">Files with Partner Data</p>
          <p className="text-2xl font-bold text-blue-600">
            {tax1065Files.filter(f => {
              const formType = f.form_type || f.data?.form_type;
              const isK1 = formType === 'K-1' || formType === 'K1';
              const has1065K1Data = formType === '1065' && f.data?.schedule_k1?.total_officer_compensation > 0;
              return isK1 || has1065K1Data;
            }).length}
          </p>
        </div>
      </div>
      
      {/* 1065 Verification Banner */}
      {has1065Verification && (
        <div className={`rounded-lg p-4 ${verificationPassed ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
          <div className="flex items-center gap-3">
            {verificationPassed ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <DollarSign className="w-5 h-5 text-amber-600" />
            )}
            <div className="flex-1">
              <p className={`font-semibold ${verificationPassed ? 'text-green-800' : 'text-amber-800'}`}>
                {verificationPassed ? '1065 Verification Passed' : '1065 Verification Mismatch'}
              </p>
              <p className={`text-sm ${verificationPassed ? 'text-green-700' : 'text-amber-700'}`}>
                1065 Total: ${form1065GrandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })} | 
                K-1 Sum: ${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                {!verificationPassed && ` (Diff: $${Math.abs(verificationDiff).toLocaleString('en-US', { minimumFractionDigits: 2 })})`}
              </p>
            </div>
          </div>
          {verificationPassed && (
            <p className="text-xs text-green-600 mt-2">
              ✓ Individual K-1 partner amounts match the Form 1065 combined total
            </p>
          )}
        </div>
      )}
      
      {/* Partner x Year Table */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-gray-700">Partner</th>
              {years.map(year => (
                <th key={year} className="text-right px-4 py-3 font-semibold text-gray-700">
                  {year}
                </th>
              ))}
              <th className="text-right px-4 py-3 font-semibold text-blue-700 bg-blue-50">
                Total
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {partners.map(partner => {
              const partnerTotal = Object.values(partnerData[partner]).reduce((sum, val) => sum + val, 0);
              
              return (
                <tr key={partner} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{partner}</td>
                  {years.map(year => (
                    <td key={year} className="text-right px-4 py-3 text-gray-700">
                      {partnerData[partner][year] 
                        ? `$${partnerData[partner][year].toLocaleString()}`
                        : '-'}
                    </td>
                  ))}
                  <td className="text-right px-4 py-3 font-semibold text-blue-700 bg-blue-50">
                    ${partnerTotal.toLocaleString()}
                  </td>
                </tr>
              );
            })}
            
            {/* Year Totals Row */}
            <tr className="bg-blue-50 font-bold">
              <td className="px-4 py-3 text-blue-900">
                Total Officer Comp (per year)
              </td>
              {years.map(year => (
                <td key={year} className="text-right px-4 py-3 text-blue-900">
                  ${yearTotals[year]?.toLocaleString() || '0'}
                </td>
              ))}
              <td className="text-right px-4 py-3 text-blue-900 bg-blue-100 text-lg">
                ${grandTotal.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <p className="text-sm font-semibold text-green-800">Partners</p>
          </div>
          <p className="text-2xl font-bold text-green-900">{partners.length}</p>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-5 h-5 text-blue-600" />
            <p className="text-sm font-semibold text-blue-800">Years Covered</p>
          </div>
          <p className="text-2xl font-bold text-blue-900">{years.length}</p>
        </div>
        
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-blue-600" />
            <p className="text-sm font-semibold text-blue-800">Total Officer Comp</p>
          </div>
          <p className="text-2xl font-bold text-blue-900">${grandTotal.toLocaleString()}</p>
        </div>
      </div>
      
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-semibold mb-1">💡 How this is used in BI:</p>
        <ul className="list-disc ml-5 space-y-1">
          <li>Total Officer Compensation per year is used in Step 1 calculations</li>
          <li>Helps determine variable vs. fixed payroll components</li>
          <li>Critical for accurate B2T (Benefit-to-Total payroll) ratio validation</li>
        </ul>
      </div>
    </div>
  );
}
